import { useEffect, useRef, useState } from 'react';

export interface DurationPickerProps {
  label?: string;
  /** Help text shown under the label. */
  hint?: string;
  /** Selected durations in minutes. The first is the default (opens first on the booking page). */
  value: number[];
  onChange: (value: number[]) => void;
  /** Preset options in minutes shown in the menu. Defaults to 15-minute steps up to 1 hr. */
  presets?: number[];
  /** Smallest custom duration allowed, in minutes (default 5). */
  minMinutes?: number;
  /** Largest custom duration allowed, in minutes (default 1440 = 24h). */
  maxMinutes?: number;
  className?: string;
}

/** Format minutes as "30 min", "1 hr", or "1 hr 30 min". */
export function formatDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h && m) return `${h} hr ${m} min`;
  if (h) return `${h} hr`;
  return `${m} min`;
}

const DEFAULT_PRESETS = [15, 30, 45, 60];

/**
 * A list of selected durations (each removable) plus an "+ Add duration" menu of presets
 * and a "Custom ›" flyout for arbitrary values. Open/close is controlled in React (no
 * Bootstrap JS). The first selected duration is treated as the default.
 */
export function DurationPicker({
  label,
  hint,
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  minMinutes = 5,
  maxMinutes = 1440,
  className = '',
}: DurationPickerProps) {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customVal, setCustomVal] = useState('');
  const [customUnit, setCustomUnit] = useState<'min' | 'hr'>('min');
  const rootRef = useRef<HTMLDivElement>(null);

  const closeAll = () => {
    setOpen(false);
    setCustomOpen(false);
    setCustomVal('');
    setCustomUnit('min');
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeAll();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAll();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const add = (min: number) => {
    if (min > 0 && !value.includes(min)) onChange([...value, min]);
    closeAll();
  };

  const remove = (min: number) => {
    if (value.length > 1) onChange(value.filter((v) => v !== min));
  };

  const customMinutes = (() => {
    const n = Number(customVal);
    if (!Number.isFinite(n) || n <= 0) return null;
    const mins = Math.round(customUnit === 'hr' ? n * 60 : n);
    if (mins < minMinutes || mins > maxMinutes || value.includes(mins)) return null;
    return mins;
  })();

  return (
    <div className={`mb-3 ${className}`.trim()}>
      {label && <label className="form-label d-block">{label}</label>}
      {hint && <div className="form-text mt-0 mb-2">{hint}</div>}

      <div className="d-flex flex-column gap-2">
        {value.map((min) => (
          <div
            key={min}
            className="form-control d-flex align-items-center justify-content-between"
            style={{ cursor: 'default' }}
          >
            <span>{formatDuration(min)}</span>
            <button
              type="button"
              className="btn btn-sm btn-icon p-0 border-0 text-muted"
              aria-label={`Remove ${formatDuration(min)}`}
              disabled={value.length <= 1}
              onClick={() => remove(min)}
              style={{ lineHeight: 1 }}
            >
              <i className="ph-x" />
            </button>
          </div>
        ))}
      </div>

      <div className="dropdown mt-2" ref={rootRef} style={{ position: 'relative' }}>
        <button
          type="button"
          className="btn btn-link p-0 text-primary text-decoration-none fw-semibold"
          aria-expanded={open}
          onClick={() => (open ? closeAll() : setOpen(true))}
        >
          <i className="ph-plus me-1" />
          Add duration
        </button>

        <div
          className={`dropdown-menu ${open ? 'show' : ''}`.trim()}
          style={{ position: 'absolute', top: '100%', left: 0, minWidth: 200, zIndex: 1030 }}
        >
          {presets.map((p) => {
            const added = value.includes(p);
            return (
              <button
                key={p}
                type="button"
                className="dropdown-item d-flex align-items-center justify-content-between"
                disabled={added}
                onClick={() => add(p)}
              >
                <span>{formatDuration(p)}</span>
                {added && <span className="text-muted fs-sm">added</span>}
              </button>
            );
          })}

          <div className="dropdown-divider" />
          <button
            type="button"
            className={`dropdown-item d-flex align-items-center justify-content-between ${customOpen ? 'active' : ''}`.trim()}
            onClick={() => setCustomOpen((o) => !o)}
          >
            <span>Custom</span>
            <i className="ph-caret-right" />
          </button>

          {customOpen && (
            <div
              className="dropdown-menu show p-3"
              style={{ position: 'absolute', top: 'auto', bottom: 0, left: '100%', minWidth: 240, zIndex: 1031 }}
            >
              <div className="fw-semibold mb-2">Add custom duration</div>
              <div className="d-flex align-items-center gap-2 mb-3">
                <input
                  type="number"
                  min={customUnit === 'hr' ? undefined : minMinutes}
                  className="form-control"
                  value={customVal}
                  autoFocus
                  onChange={(e) => setCustomVal(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && customMinutes) {
                      e.preventDefault();
                      add(customMinutes);
                    }
                  }}
                  style={{ maxWidth: 110 }}
                />
                <select
                  className="form-select"
                  value={customUnit}
                  onChange={(e) => setCustomUnit(e.target.value as 'min' | 'hr')}
                  style={{ maxWidth: 90 }}
                >
                  <option value="min">min</option>
                  <option value="hr">hr</option>
                </select>
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-light btn-sm" onClick={closeAll}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={!customMinutes}
                  onClick={() => customMinutes && add(customMinutes)}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
